import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { resolveMediaUrl } from '../utils/styleHelpers.js';
import { fetchMediaLibrary, uploadMediaFile, deleteMediaFile, IOS_FILE_INPUT_STYLE } from '../utils/imageUploadHelper.js';
import { LuFolderOpen, LuRotateCcw, LuX, LuImage, LuVideo, LuFileText, LuFileCode, LuUpload, LuTrash2, LuCheck, LuCopy } from 'react-icons/lu';

const formatBytes = (bytes, decimals = 1) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

export const MediaLibraryModal = ({
    isOpen,
    onClose,
    onSelect,
    title = 'Media Library — Select from Uploads',
    initialSelectedUrl = null,
    allowedTypes = ['image', 'video', 'document', 'template'], // filter types allowed
    allowMultiple = false
}) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'image' | 'video' | 'document'
    const [sortBy, setSortBy] = useState('newest'); // 'newest' | 'oldest' | 'name' | 'size'
    const [selectedItems, setSelectedItems] = useState([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [deletingPath, setDeletingPath] = useState(null);
    const [copiedUrl, setCopiedUrl] = useState(false);
    const fileInputRef = useRef(null);

    // Fetch media files whenever modal is opened
    const loadMedia = async () => {
        setLoading(true);
        try {
            const list = await fetchMediaLibrary();
            setFiles(list || []);
            
            // If an initialSelectedUrl was passed, pre-select it
            if (initialSelectedUrl && list && list.length > 0) {
                const match = list.find(f => 
                    f.url === initialSelectedUrl || 
                    f.path === initialSelectedUrl || 
                    initialSelectedUrl.includes(f.name)
                );
                if (match) {
                    setSelectedItems([match]);
                }
            }
        } catch (err) {
            console.error('Error loading media library:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadMedia();
            document.body.style.overflow = 'hidden';
        } else {
            setSelectedItems([]);
            setSearchQuery('');
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Handle Escape key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // File Upload Handler
    const handleFileUpload = async (e) => {
        const fileList = e.target?.files || e.dataTransfer?.files;
        if (!fileList || fileList.length === 0) return;

        setUploading(true);
        try {
            for (let i = 0; i < fileList.length; i++) {
                const f = fileList[i];
                const res = await uploadMediaFile(f);
                if (res && res.url) {
                    // Reload list and select the newly uploaded file
                    const list = await fetchMediaLibrary();
                    setFiles(list || []);
                    const newMatch = list?.find(item => item.url === res.url || res.url.includes(item.name));
                    if (newMatch) {
                        setSelectedItems([newMatch]);
                    }
                }
            }
        } catch (err) {
            console.error('Upload failed inside MediaLibraryModal:', err);
            alert('File upload failed: ' + (err.message || 'Unknown error'));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // Drag and Drop handlers
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        await handleFileUpload(e);
    };

    // Delete handler
    const handleDelete = async (fileItem) => {
        if (!fileItem) return;
        const confirmDelete = window.confirm(`Are you sure you want to permanently delete "${fileItem.name}" from the uploads directory?`);
        if (!confirmDelete) return;

        setDeletingPath(fileItem.path);
        try {
            const success = await deleteMediaFile(fileItem.path || fileItem.url);
            if (success) {
                setFiles(prev => prev.filter(f => f.path !== fileItem.path));
                setSelectedItems(prev => prev.filter(f => f.path !== fileItem.path));
            } else {
                alert('Failed to delete file from server.');
            }
        } catch (err) {
            console.error('Delete media error:', err);
            alert('Error deleting file.');
        } finally {
            setDeletingPath(null);
        }
    };

    // Item Selection
    const toggleSelect = (item) => {
        if (allowMultiple) {
            const exists = selectedItems.some(f => f.path === item.path);
            if (exists) {
                setSelectedItems(prev => prev.filter(f => f.path !== item.path));
            } else {
                setSelectedItems(prev => [...prev, item]);
            }
        } else {
            setSelectedItems([item]);
        }
    };

    // Confirm selection
    const handleConfirm = () => {
        if (selectedItems.length === 0) return;
        if (allowMultiple) {
            const urls = selectedItems.map(item => item.url);
            onSelect(urls, selectedItems);
        } else {
            const item = selectedItems[0];
            onSelect(item.url, item);
        }
        onClose();
    };

    // Copy URL
    const handleCopyUrl = (url) => {
        const full = resolveMediaUrl(url);
        navigator.clipboard?.writeText(full || url);
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
    };

    // Filter & Sort
    const filteredFiles = useMemo(() => {
        let result = [...files];

        // Type filter
        if (typeFilter !== 'all') {
            if (typeFilter === 'template') {
                result = result.filter(f => f.ext === 'json' || f.type === 'template');
            } else if (typeFilter === 'document') {
                result = result.filter(f => f.type === 'document' || f.ext === 'pdf' || f.ext === 'txt' || f.ext === 'csv');
            } else {
                result = result.filter(f => f.type === typeFilter);
            }
        }

        // Search query
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            result = result.filter(f => 
                (f.name && f.name.toLowerCase().includes(q)) || 
                (f.path && f.path.toLowerCase().includes(q))
            );
        }

        // Sort
        if (sortBy === 'newest') {
            result.sort((a, b) => (b.modified || 0) - (a.modified || 0));
        } else if (sortBy === 'oldest') {
            result.sort((a, b) => (a.modified || 0) - (b.modified || 0));
        } else if (sortBy === 'name') {
            result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        } else if (sortBy === 'size') {
            result.sort((a, b) => (b.size || 0) - (a.size || 0));
        }

        return result;
    }, [files, typeFilter, searchQuery, sortBy]);

    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef({ mouseX: 0, mouseY: 0, startX: 0, startY: 0 });
    const [isMobileScreen, setIsMobileScreen] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobileScreen(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Reset drag position when modal opens
    useEffect(() => {
        if (isOpen) {
            setDragOffset({ x: 0, y: 0 });
        }
    }, [isOpen]);

    // Handle drag start
    const handleDragStart = (e) => {
        if (e.target.closest('button, input, select, a, label')) return;
        const clientX = e.clientX !== undefined ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
        const clientY = e.clientY !== undefined ? e.clientY : (e.touches && e.touches[0] ? e.touches[0].clientY : 0);

        dragStartRef.current = {
            mouseX: clientX,
            mouseY: clientY,
            startX: dragOffset.x,
            startY: dragOffset.y
        };
        setIsDragging(true);
    };

    useEffect(() => {
        if (!isDragging) return;

        const handleDragMove = (e) => {
            const clientX = e.clientX !== undefined ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
            const clientY = e.clientY !== undefined ? e.clientY : (e.touches && e.touches[0] ? e.touches[0].clientY : 0);

            const dx = clientX - dragStartRef.current.mouseX;
            const dy = clientY - dragStartRef.current.mouseY;

            const maxDeltaX = window.innerWidth * 0.48;
            const maxDeltaY = window.innerHeight * 0.48;
            const clampedX = Math.max(-maxDeltaX, Math.min(maxDeltaX, dragStartRef.current.startX + dx));
            const clampedY = Math.max(-maxDeltaY, Math.min(maxDeltaY, dragStartRef.current.startY + dy));

            setDragOffset({ x: clampedX, y: clampedY });
        };

        const handleDragEnd = () => {
            setIsDragging(false);
        };

        window.addEventListener('mousemove', handleDragMove, { passive: true });
        window.addEventListener('mouseup', handleDragEnd);
        window.addEventListener('touchmove', handleDragMove, { passive: true });
        window.addEventListener('touchend', handleDragEnd);

        return () => {
            window.removeEventListener('mousemove', handleDragMove);
            window.removeEventListener('mouseup', handleDragEnd);
            window.removeEventListener('touchmove', handleDragMove);
            window.removeEventListener('touchend', handleDragEnd);
        };
    }, [isDragging]);

    if (!isOpen) return null;

    const currentSelected = selectedItems.length > 0 ? selectedItems[selectedItems.length - 1] : null;

    const modalContent = (
        <div
            data-draggable-toolbar="true"
            data-modal-portal="true"
            className="media-library-modal-portal media-library-backdrop"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 2147483640,
                backgroundColor: 'rgba(15, 23, 42, 0.75)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: isMobileScreen ? '8px' : '16px',
                boxSizing: 'border-box'
            }}
            onMouseDown={(e) => {
                if (e.target !== e.currentTarget) {
                    e.stopPropagation();
                }
            }}
            onClick={onClose}
        >
            <div
                data-draggable-toolbar="true"
                data-modal-portal="true"
                className="media-library-modal-container"
                style={{
                    backgroundColor: '#ffffff',
                    width: '100%',
                    maxWidth: '1080px',
                    maxHeight: isMobileScreen ? '96vh' : '92vh',
                    borderRadius: '16px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    border: '1px solid #e2e8f0',
                    transform: `translate3d(${dragOffset.x}px, ${dragOffset.y}px, 0)`,
                    transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                    animation: 'omniModalPop 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {/* 1. MODAL HEADER (DRAGGABLE) */}
                <div 
                    onMouseDown={handleDragStart}
                    onTouchStart={handleDragStart}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: isMobileScreen ? '12px 16px' : '14px 20px',
                        borderBottom: '1px solid #e2e8f0',
                        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
                        cursor: isDragging ? 'grabbing' : 'grab',
                        userSelect: 'none'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <LuFolderOpen size={22} style={{ color: '#2563eb' }} />
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <h3 style={{ margin: 0, fontSize: isMobileScreen ? '1rem' : '1.15rem', fontWeight: '700', color: '#0f172a' }}>
                                    {title}
                                </h3>
                                <span style={{ fontSize: '0.72rem', color: '#64748b', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '1px 6px', display: isMobileScreen ? 'none' : 'inline-block' }}>
                                    ⋮⋮ Drag to move
                                </span>
                            </div>
                            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                {files.length} {files.length === 1 ? 'file' : 'files'} in <code style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: '4px', fontSize: '0.72rem' }}>/uploads/</code>
                            </span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {(dragOffset.x !== 0 || dragOffset.y !== 0) && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDragOffset({ x: 0, y: 0 });
                                }}
                                title="Center modal position"
                                style={{
                                    background: '#f1f5f9',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '8px',
                                    padding: '5px 10px',
                                    cursor: 'pointer',
                                    fontSize: '0.78rem',
                                    color: '#475569',
                                    fontWeight: '600',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                }}
                            >
                                <LuRotateCcw size={12} /> Center
                            </button>
                        )}

                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                loadMedia();
                            }}
                            title="Refresh media list"
                            style={{
                                background: '#f1f5f9',
                                border: '1px solid #cbd5e1',
                                borderRadius: '8px',
                                padding: '5px 10px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                color: '#475569',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                fontWeight: '600'
                            }}
                        >
                            <LuRotateCcw size={13} /> Refresh
                        </button>

                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#94a3b8',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <LuX size={18} />
                        </button>
                    </div>
                </div>

                {/* 2. TOOLBAR & FILTERS */}
                <div style={{
                    padding: isMobileScreen ? '10px 14px' : '12px 24px',
                    borderBottom: '1px solid #e2e8f0',
                    background: '#f8fafc',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: isMobileScreen ? '8px' : '12px'
                }}>
                    {/* Search & Type filter */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: isMobileScreen ? '1 1 100%' : '1 1 320px', flexWrap: isMobileScreen ? 'wrap' : 'nowrap' }}>
                        <div style={{ position: 'relative', flex: '1 1 180px' }}>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search media by name..."
                                style={{
                                    width: '100%',
                                    padding: '7px 12px',
                                    paddingRight: searchQuery ? '28px' : '12px',
                                    borderRadius: '8px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '0.85rem',
                                    boxSizing: 'border-box',
                                    background: '#ffffff',
                                    color: '#0f172a'
                                }}
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    style={{
                                        position: 'absolute',
                                        right: '6px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#94a3b8',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '2px'
                                    }}
                                >
                                    <LuX size={12} />
                                </button>
                            )}
                        </div>

                        {/* Type Filters */}
                        <div style={{ display: 'inline-flex', background: '#e2e8f0', padding: '3px', borderRadius: '8px', gap: '2px', overflowX: 'auto', maxWidth: '100%' }}>
                            {[
                                { id: 'all', label: 'All', icon: null },
                                { id: 'image', label: 'Images', icon: LuImage },
                                { id: 'video', label: 'Videos', icon: LuVideo },
                                { id: 'document', label: 'PDFs & Docs', icon: LuFileText },
                                { id: 'template', label: 'Templates', icon: LuFileCode }
                            ].map(tab => {
                                const TabIcon = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        onClick={() => setTypeFilter(tab.id)}
                                        style={{
                                            border: 'none',
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                            fontSize: '0.75rem',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            background: typeFilter === tab.id ? '#ffffff' : 'transparent',
                                            color: typeFilter === tab.id ? '#1e293b' : '#64748b',
                                            boxShadow: typeFilter === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                            whiteSpace: 'nowrap',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                    >
                                        {TabIcon && <TabIcon size={12} />}
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Sort & Upload Action */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            style={{
                                padding: '6px 10px',
                                borderRadius: '8px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.8rem',
                                background: '#ffffff',
                                color: '#334155',
                                cursor: 'pointer',
                                fontWeight: '500'
                            }}
                        >
                            <option value="newest">Newest First</option>
                            <option value="oldest">Oldest First</option>
                            <option value="name">Name (A-Z)</option>
                            <option value="size">Size (Largest)</option>
                        </select>

                        <label
                            style={{
                                background: '#2563eb',
                                color: '#ffffff',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                fontSize: '0.82rem',
                                fontWeight: '600',
                                cursor: uploading ? 'wait' : 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)',
                                transition: 'background 0.15s ease'
                            }}
                        >
                            {uploading ? 'Uploading...' : <><LuUpload size={13} /> Upload</>}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,video/*,.pdf,.ico,.json,.txt,.csv"
                                multiple
                                onChange={handleFileUpload}
                                style={IOS_FILE_INPUT_STYLE}
                                disabled={uploading}
                            />
                        </label>
                    </div>
                </div>

                {/* 3. MAIN BODY (GRID + PREVIEW DRAWER) */}
                <div style={{
                    display: 'flex',
                    flexDirection: isMobileScreen ? 'column' : 'row',
                    flex: 1,
                    overflow: 'hidden',
                    position: 'relative',
                    minHeight: isMobileScreen ? '240px' : '360px',
                    maxHeight: isMobileScreen ? '52vh' : '60vh'
                }}>
                    {/* Drag-over Overlay */}
                    {isDragOver && (
                        <div style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 10,
                            backgroundColor: 'rgba(37, 99, 235, 0.12)',
                            border: '3px dashed #2563eb',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#1d4ed8',
                            pointerEvents: 'none',
                            backdropFilter: 'blur(2px)'
                        }}>
                            <span style={{ fontSize: '2.5rem', marginBottom: '8px' }}>📥</span>
                            <span style={{ fontSize: '1.2rem', fontWeight: '700' }}>Drop files to upload immediately to /uploads/</span>
                        </div>
                    )}

                    {/* Media Grid Section */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: isMobileScreen ? '10px 12px' : '16px 20px',
                        background: '#ffffff'
                    }}>
                        {loading ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b', gap: '12px' }}>
                                <span style={{ fontSize: '2rem' }}>⏳</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>Loading uploads media library...</span>
                            </div>
                        ) : filteredFiles.length === 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', gap: '12px', textAlign: 'center', padding: '40px 20px' }}>
                                <span style={{ fontSize: '3rem' }}>📂</span>
                                <h4 style={{ margin: 0, color: '#475569', fontSize: '1.1rem' }}>No media files found</h4>
                                <p style={{ margin: 0, fontSize: '0.85rem', maxWidth: '340px' }}>
                                    {searchQuery ? `No files matching "${searchQuery}".` : 'Drag and drop images or click "Upload New" above to add your first image.'}
                                </p>
                            </div>
                        ) : (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: isMobileScreen ? 'repeat(auto-fill, minmax(105px, 1fr))' : 'repeat(auto-fill, minmax(130px, 1fr))',
                                gap: isMobileScreen ? '10px' : '14px'
                            }}>
                                {filteredFiles.map((file) => {
                                    const isSelected = selectedItems.some(f => f.path === file.path);
                                    const resolved = resolveMediaUrl(file.url);
                                    const isImg = file.type === 'image';
                                    const isVid = file.type === 'video';
                                    const isDoc = file.type === 'document';

                                    return (
                                        <div
                                            key={file.path || file.name}
                                            onClick={() => toggleSelect(file)}
                                            onDoubleClick={handleConfirm}
                                            style={{
                                                position: 'relative',
                                                borderRadius: '10px',
                                                border: isSelected ? '2px solid #2563eb' : '1px solid #e2e8f0',
                                                backgroundColor: isSelected ? '#eff6ff' : '#f8fafc',
                                                cursor: 'pointer',
                                                overflow: 'hidden',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                transition: 'all 0.15s ease',
                                                boxShadow: isSelected ? '0 0 0 3px rgba(37, 99, 235, 0.2)' : '0 1px 3px rgba(0,0,0,0.05)'
                                            }}
                                        >
                                            {/* Selection Checkmark */}
                                            {isSelected && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '6px',
                                                    left: '6px',
                                                    zIndex: 2,
                                                    background: '#2563eb',
                                                    color: '#ffffff',
                                                    borderRadius: '50%',
                                                    width: '20px',
                                                    height: '20px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '11px',
                                                    fontWeight: 'bold',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                }}>
                                                    ✓
                                                </div>
                                            )}

                                            {/* Type badge */}
                                            <div style={{
                                                position: 'absolute',
                                                top: '6px',
                                                right: '6px',
                                                zIndex: 2,
                                                background: 'rgba(15, 23, 42, 0.65)',
                                                color: '#ffffff',
                                                borderRadius: '4px',
                                                padding: '2px 5px',
                                                fontSize: '9px',
                                                fontWeight: 'bold',
                                                textTransform: 'uppercase',
                                                backdropFilter: 'blur(2px)'
                                            }}>
                                                {file.ext || file.type}
                                            </div>

                                            {/* Thumbnail Area */}
                                            <div style={{
                                                height: isMobileScreen ? '85px' : '110px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: '#f1f5f9',
                                                overflow: 'hidden'
                                            }}>
                                                {isImg ? (
                                                    <img
                                                        src={resolved}
                                                        alt={file.name}
                                                        loading="lazy"
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                ) : isVid ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', color: '#6366f1' }}>
                                                        <LuVideo size={30} />
                                                        <span style={{ fontSize: '9px', fontWeight: 'bold' }}>VIDEO</span>
                                                    </div>
                                                ) : file.ext === 'json' || file.type === 'template' ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', color: '#0284c7' }}>
                                                        <LuFileCode size={30} />
                                                        <span style={{ fontSize: '9px', fontWeight: 'bold' }}>TEMPLATE</span>
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', color: '#dc2626' }}>
                                                        <LuFileText size={30} />
                                                        <span style={{ fontSize: '9px', fontWeight: 'bold' }}>DOCUMENT</span>
                                                    </div>
                                                )}
                                                <div style={{ display: 'none', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', color: '#94a3b8' }}>
                                                    <LuImage size={30} />
                                                </div>
                                            </div>

                                            {/* File info footer */}
                                            <div style={{
                                                padding: isMobileScreen ? '6px 8px' : '8px 10px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '2px'
                                            }}>
                                                <span
                                                    title={file.name}
                                                    style={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: '600',
                                                        color: '#1e293b',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                >
                                                    {file.name}
                                                </span>
                                                <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                                                    {formatBytes(file.size)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Details Sidebar / Bottom Drawer */}
                    {currentSelected && (
                        <div style={{
                            width: isMobileScreen ? '100%' : '280px',
                            maxHeight: isMobileScreen ? '180px' : 'none',
                            borderLeft: isMobileScreen ? 'none' : '1px solid #e2e8f0',
                            borderTop: isMobileScreen ? '1px solid #e2e8f0' : 'none',
                            background: '#f8fafc',
                            padding: isMobileScreen ? '10px 14px' : '16px',
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: isMobileScreen ? 'row' : 'column',
                            alignItems: isMobileScreen ? 'center' : 'stretch',
                            gap: '12px',
                            flexShrink: 0
                        }}>
                            {!isMobileScreen && (
                                <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#0f172a' }}>
                                    File Details
                                </h4>
                            )}

                            {/* Preview box */}
                            <div style={{
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: '1px solid #cbd5e1',
                                background: '#ffffff',
                                width: isMobileScreen ? '80px' : '100%',
                                height: isMobileScreen ? '80px' : 'auto',
                                maxHeight: '160px',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {currentSelected.type === 'video' ? (
                                    <video
                                        src={resolveMediaUrl(currentSelected.url)}
                                        controls={!isMobileScreen}
                                        style={{ width: '100%', height: '100%', maxHeight: '160px', objectFit: 'contain' }}
                                    />
                                ) : currentSelected.type === 'image' ? (
                                    <img
                                        src={resolveMediaUrl(currentSelected.url)}
                                        alt={currentSelected.name}
                                        style={{ width: '100%', height: '100%', maxHeight: '160px', objectFit: 'contain' }}
                                    />
                                ) : (
                                    <div style={{ padding: '10px', textAlign: 'center', color: '#dc2626', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                        <LuFileText size={32} />
                                        {!isMobileScreen && <div style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>PDF Document</div>}
                                    </div>
                                )}
                            </div>

                            {/* Info Fields */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobileScreen ? '4px' : '8px', fontSize: '0.8rem', flex: 1 }}>
                                <div>
                                    <span style={{ color: '#64748b', display: 'block', fontSize: '0.68rem', fontWeight: 'bold' }}>FILE NAME</span>
                                    <span style={{ color: '#0f172a', fontWeight: '600', wordBreak: 'break-all', fontSize: '0.78rem' }}>{currentSelected.name}</span>
                                </div>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <div>
                                        <span style={{ color: '#64748b', display: 'block', fontSize: '0.68rem', fontWeight: 'bold' }}>SIZE</span>
                                        <span style={{ color: '#0f172a', fontSize: '0.75rem' }}>{formatBytes(currentSelected.size)}</span>
                                    </div>
                                    {currentSelected.modified && !isMobileScreen && (
                                        <div>
                                            <span style={{ color: '#64748b', display: 'block', fontSize: '0.68rem', fontWeight: 'bold' }}>UPLOADED</span>
                                            <span style={{ color: '#0f172a', fontSize: '0.75rem' }}>{formatDate(currentSelected.modified)}</span>
                                        </div>
                                    )}
                                </div>
                                {!isMobileScreen && (
                                    <div>
                                        <span style={{ color: '#64748b', display: 'block', fontSize: '0.68rem', fontWeight: 'bold' }}>RELATIVE URL</span>
                                        <div style={{ display: 'flex', gap: '4px', marginTop: '3px' }}>
                                            <input
                                                type="text"
                                                readOnly
                                                value={currentSelected.url}
                                                style={{
                                                    width: '100%',
                                                    padding: '4px 8px',
                                                    fontSize: '0.75rem',
                                                    borderRadius: '4px',
                                                    border: '1px solid #cbd5e1',
                                                    background: '#ffffff',
                                                    color: '#334155'
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleCopyUrl(currentSelected.url)}
                                                title="Copy media URL"
                                                style={{
                                                    padding: '4px 8px',
                                                    fontSize: '0.75rem',
                                                    borderRadius: '4px',
                                                    border: '1px solid #cbd5e1',
                                                    background: copiedUrl ? '#dcfce7' : '#ffffff',
                                                    color: copiedUrl ? '#15803d' : '#334155',
                                                    cursor: 'pointer',
                                                    whiteSpace: 'nowrap',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                {copiedUrl ? <><LuCheck size={12} /> Copied</> : <><LuCopy size={12} /> Copy</>}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Delete button */}
                            <div style={{ marginTop: isMobileScreen ? 0 : 'auto', paddingTop: isMobileScreen ? 0 : '12px', borderTop: isMobileScreen ? 'none' : '1px solid #e2e8f0' }}>
                                <button
                                    type="button"
                                    onClick={() => handleDelete(currentSelected)}
                                    disabled={deletingPath === currentSelected.path}
                                    style={{
                                        width: isMobileScreen ? 'auto' : '100%',
                                        padding: isMobileScreen ? '5px 10px' : '7px',
                                        background: '#fef2f2',
                                        color: '#ef4444',
                                        border: '1px solid #fecaca',
                                        borderRadius: '6px',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {deletingPath === currentSelected.path ? 'Deleting...' : <><LuTrash2 size={13} /> Delete</>}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* 4. MODAL FOOTER */}
                <div style={{
                    padding: isMobileScreen ? '10px 14px' : '14px 24px',
                    borderTop: '1px solid #e2e8f0',
                    background: '#f8fafc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px'
                }}>
                    <div style={{ fontSize: isMobileScreen ? '0.78rem' : '0.85rem', color: '#64748b' }}>
                        {selectedItems.length > 0 ? (
                            <span>
                                Selected: <strong>{selectedItems.length}</strong> item{selectedItems.length > 1 ? 's' : ''} ({currentSelected?.name})
                            </span>
                        ) : (
                            <span>Click any image to select it</span>
                        )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                background: '#ffffff',
                                border: '1px solid #cbd5e1',
                                borderRadius: '8px',
                                padding: isMobileScreen ? '6px 12px' : '8px 16px',
                                fontSize: isMobileScreen ? '0.8rem' : '0.88rem',
                                fontWeight: '600',
                                color: '#475569',
                                cursor: 'pointer'
                            }}
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            onClick={handleConfirm}
                            disabled={selectedItems.length === 0}
                            style={{
                                background: selectedItems.length > 0 ? '#2563eb' : '#94a3b8',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '8px',
                                padding: isMobileScreen ? '6px 14px' : '8px 20px',
                                fontSize: isMobileScreen ? '0.8rem' : '0.88rem',
                                fontWeight: '600',
                                cursor: selectedItems.length > 0 ? 'pointer' : 'not-allowed',
                                boxShadow: selectedItems.length > 0 ? '0 2px 4px rgba(37, 99, 235, 0.25)' : 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <LuCheck size={16} /> Insert Selected Media
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    if (typeof document !== 'undefined') {
        return createPortal(modalContent, document.body);
    }
    return modalContent;
};

export default MediaLibraryModal;
